import {
  Banner,
  EmptyState,
  FieldWrapper,
  FormField,
  FormGrid,
  Page,
  Spinner,
  Tag,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { config } from 'lib-shared-backend-config';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';

import { ApiToken } from 'apis/auth';
import { usersApi } from 'apis/users';
import { getUserDisplayName } from 'utils/user';

import { SettingsIntegrationsDocumentationButton } from '../../../../../../components/documentationButton/documentationButton';
import { apiTokenTypesByVersion } from '../../../../utils/tokenType';

import { TokenFieldTemplate } from './components/tokenFieldTemplate/tokenFieldTemplate';

type ViewTokenProps = {
  token?: ApiToken;
};

export const ViewToken = ({ token }: ViewTokenProps): JSX.Element => {
  if (!token) {
    return (
      <EmptyState
        title="Something went wrong"
        description="Check if the token id in the URL is correct."
      />
    );
  }

  const { data, isLoading } = usersApi.useReadUserQuery({
    id: token.created_by,
  });

  const tokenType = token.version
    ? apiTokenTypesByVersion[token.version]
    : null;

  const displayTokenCreator = (user?: User) =>
    user ? getUserDisplayName(user) : token.created_by;

  return (
    <Page.Section
      title={`View ${token.name}`}
      titleMeta={
        <SettingsIntegrationsDocumentationButton
          onClick={() => {
            ampli.settingsIntegrationsApiTokensDocumentationLinkIntegrationsApi();

            window.open(
              `https://${config.domain.integrations}/openapi`,
              '_blank',
              'noopener,noreferrer',
            );
          }}
        />
      }
    >
      <FormGrid>
        <FormField>
          <FieldWrapper label="Created">
            {isLoading ? (
              <Spinner size={16} />
            ) : (
              <>
                <Timestamp time={token.created_at} /> by{' '}
                {displayTokenCreator(data?.data)}
              </>
            )}
          </FieldWrapper>
        </FormField>

        {tokenType && (
          <FormField>
            <FieldWrapper label="Version">
              <Tag color={tokenType.color}>{tokenType.label}</Tag>
            </FieldWrapper>
          </FormField>
        )}

        <FormField>
          <TokenFieldTemplate value={token.value} label="Token" />
        </FormField>
      </FormGrid>

      <Banner
        variant="announcement"
        headline="Token access rights"
        description={
          <>
            The API token is bound to the Administrator account that created it,
            giving integrations full access to your Dixa account. Be very
            careful when handling it and store it securely.
          </>
        }
      />
    </Page.Section>
  );
};
